import { useCallback, useEffect, useRef, useState } from 'react';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { getVolumeFromSeriesId } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { getWorldToIndex } from '@features/view/Surface3DUtilities';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import { Vector3 } from '@kitware/vtk.js/types';
import {
  SurfaceContext,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { createMeasurementActors } from '@features/view/Shared3DUtilities';

type MeasurementLabelState = {
  worldPoint: Vector3;
  totalDistance3D: number;
};

export type MeasurementLabelItem = {
  measurementKey: string;
  x: number;
  y: number;
  text: string;
};

export class MeasurementLabelStateStore {
  private stateMap = new Map<string, MeasurementLabelState>();

  set(measurementKey: string, worldPoint: Vector3, totalDistance3D: number) {
    this.stateMap.set(measurementKey, {
      worldPoint: [worldPoint[0], worldPoint[1], worldPoint[2]],
      totalDistance3D,
    });
  }

  clear() {
    this.stateMap.clear();
  }

  isEmpty() {
    return this.stateMap.size === 0;
  }

  toLabelItems(
    openGLRenderWindow: vtkOpenGLRenderWindow,
    renderer: vtkRenderer,
  ): MeasurementLabelItem[] {
    const [, height] = openGLRenderWindow.getSize();
    const items: MeasurementLabelItem[] = [];

    this.stateMap.forEach((value, measurementKey) => {
      // Measurement line actor uses flipped X in world space.
      const renderWorld: Vector3 = [
        -value.worldPoint[0],
        value.worldPoint[1],
        value.worldPoint[2],
      ];

      const display = openGLRenderWindow.worldToDisplay(
        renderWorld[0],
        renderWorld[1],
        renderWorld[2],
        renderer,
      ) as [number, number, number];

      const x = display[0];
      const y = height - display[1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      items.push({
        measurementKey,
        x,
        y,
        text: `${value.totalDistance3D.toFixed(2)} mm`,
      });
    });

    return items;
  }
}

type MeasurementPoints = Vector3[][];
type MeasurementHistoryStack = MeasurementPoints[];

type MeasurementHistory = {
  undoStack: MeasurementHistoryStack;
  redoStack: MeasurementHistoryStack;
};

type ContextWithMeasurementHistory = (SurfaceContext | VRContext) & {
  measurementHistory?: MeasurementHistory;
};

type params = {
  context: React.MutableRefObject<SurfaceContext | VRContext | null>;
  lowQuality: boolean;
};

const MAX_HISTORY = 100;

const clonePoints = (points: Vector3[][]) =>
  points.map((line) =>
    line.map((point) => [point[0], point[1], point[2]] as Vector3),
  );

const distance3D = (p1: Vector3, p2: Vector3, spacing: Vector3) => {
  const dx = (p2[0] - p1[0]) * spacing[0];
  const dy = (p2[1] - p1[1]) * spacing[1];
  const dz = (p2[2] - p1[2]) * spacing[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

export function useMeasurementOverlayState({ context, lowQuality }: params) {
  const { panelToolState } = useAnalysisPage();
  const { selectedSeriesId } = useBrowserPage();
  const measurementPointsRef = useRef<Vector3[][]>([]);
  const measurementLabelStateStoreRef = useRef(
    new MeasurementLabelStateStore(),
  );
  const measurementSvgRef = useRef<SVGSVGElement | null>(null);
  const [measurementMode, setMeasurementModeState] = useState(false);
  const [measurementLabelItems, setMeasurementLabelItems] = useState<
    MeasurementLabelItem[]
  >([]);
  const isMeasurement3DEnabled =
    panelToolState?.activeToolId === 'Measurement3D';

  const getMeasurementHistory = useCallback(() => {
    const ctx = context.current as ContextWithMeasurementHistory | null;
    if (!ctx) return null;
    if (!ctx.measurementHistory) {
      ctx.measurementHistory = {
        undoStack: [],
        redoStack: [],
      };
    }
    return ctx.measurementHistory;
  }, []);

  const clearMeasurementHistory = useCallback(() => {
    if (!context.current) return null;
    context.current.measurementHistory = {
      undoStack: [],
      redoStack: [],
    };
    measurementLabelStateStoreRef.current.clear();
    measurementPointsRef.current = [];
    setMeasurementLabelItems([]);
  }, []);

  const updateMeasurementLabelPositions = useCallback(() => {
    if (!context.current) return;
    if (measurementLabelStateStoreRef.current.isEmpty()) {
      setMeasurementLabelItems((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const { renderWindow, renderer, overlayRenderer } = context.current;
    const projectionRenderer = overlayRenderer ?? renderer;
    if (!renderWindow || !projectionRenderer) return;

    const openGLRenderWindow = renderWindow.getViews?.()[0] as
      | vtkOpenGLRenderWindow
      | undefined;
    if (!openGLRenderWindow?.worldToDisplay) return;
    const nextLabelItems = measurementLabelStateStoreRef.current.toLabelItems(
      openGLRenderWindow,
      projectionRenderer,
    );
    setMeasurementLabelItems(nextLabelItems);
  }, []);

  const updateMeasurementDistanceLabel = useCallback(
    (measurementKey: string, worldPoint: Vector3, totalDistance3D: number) => {
      measurementLabelStateStoreRef.current.set(
        measurementKey,
        worldPoint,
        totalDistance3D,
      );
      updateMeasurementLabelPositions();
    },
    [updateMeasurementLabelPositions],
  );

  const recordMeasurementHistory = useCallback(() => {
    const history = getMeasurementHistory();
    if (!history) return;
    history.undoStack.push(clonePoints(measurementPointsRef.current));
    if (history.undoStack.length > MAX_HISTORY) {
      history.undoStack.shift();
    }
    history.redoStack.length = 0;
  }, [getMeasurementHistory]);

  const upsertMeasurementActors = useCallback(
    ({
      points,
      index,
      actorMap,
      overlayRenderer,
      origin,
      spacing,
    }: {
      points: Vector3[];
      index: number;
      actorMap: Record<string, vtkActor | undefined>;
      overlayRenderer: vtkRenderer;
      origin: Vector3;
      spacing: Vector3;
    }) => {
      if (points.length < 2 || !context.current?.actorIds) return null;

      const { lineActor, keyName, pointsKey } = createMeasurementActors(
        points,
        index,
        overlayRenderer,
        actorMap,
      );
      let totalDistance3D = 0;
      let prevIndex = getWorldToIndex(
        points[0],
        spacing,
        origin,
        lowQuality,
      ) as Vector3;

      for (let i = 1; i < points.length; i++) {
        const next = getWorldToIndex(
          points[i],
          spacing,
          origin,
          lowQuality,
        ) as Vector3;
        totalDistance3D += distance3D(prevIndex, next, spacing);
        prevIndex = next;
      }

      lineActor.set({ totalDistance3D });
      measurementLabelStateStoreRef.current.set(
        keyName,
        points[points.length - 1],
        totalDistance3D,
      );
      return { keyName, pointsKey };
    },
    [],
  );

  const rebuildMeasurementActors = useCallback(() => {
    const ctx = context.current;
    if (!ctx) return;
    const { overlayRenderer, actorIds, renderWindow } = ctx;
    if (!overlayRenderer || !actorIds) return;

    const actorMap = actorIds as Record<string, vtkActor | undefined>;
    measurementLabelStateStoreRef.current.clear();
    setMeasurementLabelItems([]);

    const volume = selectedSeriesId
      ? getVolumeFromSeriesId(selectedSeriesId, lowQuality)
      : undefined;

    if (!volume) return;
    const nextActorKeys = new Set<string>();
    const { origin, spacing } = volume;

    measurementPointsRef.current.forEach((points, index) => {
      const keys = upsertMeasurementActors({
        points,
        index,
        actorMap,
        overlayRenderer,
        origin,
        spacing,
      });
      if (!keys) return;
      nextActorKeys.add(keys.keyName);
      nextActorKeys.add(keys.pointsKey);
    });

    Object.keys(actorMap).forEach((key) => {
      if (!key.startsWith('measurement.')) return;
      if (nextActorKeys.has(key)) return;
      const actor = actorMap[key] as vtkActor | undefined;
      if (actor) overlayRenderer.removeActor(actor);
      delete actorMap[key];
    });

    updateMeasurementLabelPositions();
    renderWindow?.render?.();
  }, [updateMeasurementLabelPositions, upsertMeasurementActors]);

  const renderMeasurementLine = useCallback(
    (points: Vector3[], index: number) => {
      const ctx = context.current;
      if (!ctx) return;
      const { overlayRenderer, actorIds, renderWindow } = ctx;
      if (!overlayRenderer || !actorIds) return;

      const volume = selectedSeriesId
        ? getVolumeFromSeriesId(selectedSeriesId, lowQuality)
        : undefined;
      const actorMap = actorIds as Record<string, vtkActor | undefined>;
      if (!volume) return;
      const { origin, spacing } = volume;

      const keys = upsertMeasurementActors({
        points,
        index,
        actorMap,
        overlayRenderer,
        origin,
        spacing,
      });
      if (!keys) return;
      updateMeasurementLabelPositions();
      renderWindow?.render?.();
    },
    [updateMeasurementLabelPositions, upsertMeasurementActors],
  );

  const undoMeasurement = useCallback(() => {
    const history = getMeasurementHistory();
    if (!history || history.undoStack.length === 0) return;
    history.redoStack.push(clonePoints(measurementPointsRef.current));
    const prev = history.undoStack.pop();
    if (!prev) return;
    const nextPoints = clonePoints(prev);
    if (nextPoints.length > 0 && nextPoints[nextPoints.length - 1].length <= 1) {
      nextPoints.pop();
    }
    measurementPointsRef.current = nextPoints;
    rebuildMeasurementActors();
  }, [getMeasurementHistory, rebuildMeasurementActors]);

  const redoMeasurement = useCallback(() => {
    const history = getMeasurementHistory();
    if (!history || history.redoStack.length === 0) return;
    history.undoStack.push(clonePoints(measurementPointsRef.current));
    const next = history.redoStack.pop();
    if (!next) return;
    measurementPointsRef.current = clonePoints(next);
    rebuildMeasurementActors();
  }, [getMeasurementHistory, rebuildMeasurementActors]);
  useEffect(() => {
    const nextMode = isMeasurement3DEnabled;
    setMeasurementModeState((prevMode) => {
      const points = measurementPointsRef.current;
      if (
        (prevMode && !nextMode) ||
        (!prevMode && nextMode && points.length === 0)
      ) {
        recordMeasurementHistory();
        points.push([]);
      }
      return nextMode;
    });
  }, [isMeasurement3DEnabled, recordMeasurementHistory]);

  useEffect(() => {
    if (!context.current) return;
    const projectionRenderer =
      context.current.overlayRenderer ?? context.current.renderer;
    const camera = projectionRenderer.getActiveCamera();
    const sync = () => {
      if (!context.current || measurementLabelStateStoreRef.current.isEmpty())
        return;
      updateMeasurementLabelPositions();
    };
    const subscription = camera.onModified(sync);
    return () => {
      subscription?.unsubscribe?.();
    };
  }, [updateMeasurementLabelPositions]);

  return {
    measurementPointsRef,
    measurementLabelStateStoreRef,
    measurementSvgRef,
    measurementMode,
    measurementLabelItems,
    setMeasurementLabelItems,
    updateMeasurementLabelPositions,
    updateMeasurementDistanceLabel,
    recordMeasurementHistory,
    renderMeasurementLine,
    redoMeasurement,
    undoMeasurement,
    clearMeasurementHistory,
  };
}
